import React from 'react';
import { useChildren } from '../context/ChildProvider';
import { useNotifications } from '../hooks/useNotifications';
import { Spinner } from '../components/UI';

const NotificationItem = ({ title, description, date, icon, read, onClick }) => (
    <div 
        onClick={onClick} 
        className={`group flex items-start space-x-4 p-5 rounded-[1.5rem] cursor-pointer transition-all duration-500 border backdrop-blur-xl
            ${read 
                ? 'bg-white/40 border-white/50 opacity-80' 
                : 'bg-white/70 border-white shadow-[0_8px_30px_rgba(0,0,0,0.06)] transform hover:-translate-y-1'
            }
        `}
    >
        <div className={`
            flex-shrink-0 h-12 w-12 rounded-[1.25rem] flex items-center justify-center text-xl transition-all duration-300
            ${read 
                ? 'bg-gray-100 text-gray-400' 
                : 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-sm group-hover:scale-110 group-hover:rotate-3'
            }
        `}>
            {/* Convert SVG string to real SVG if needed, but since it's a string from hook: */}
            <div dangerouslySetInnerHTML={{ __html: icon }} className="w-6 h-6 flex items-center justify-center" />
        </div>
        <div className="flex-1">
            <div className="flex justify-between items-start">
                <h4 className={`text-[15px] font-extrabold tracking-tight ${read ? 'text-gray-500' : 'text-gray-800'}`}>
                    {title}
                </h4>
                {!read && (
                    <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.8)] animate-pulse mt-1"></span>
                )}
            </div>
            <p className={`text-[12px] leading-relaxed mt-1 ${read ? 'text-gray-400' : 'text-gray-600'}`}>{description}</p>
            <p className={`text-[10px] font-bold mt-2 tracking-wider uppercase ${read ? 'text-gray-300' : 'text-indigo-400'}`}>{date}</p>
        </div>
    </div>
);

export default function NotificationScreen({ user, navigate }) {
    const { myChildren, loading: loadingChildren } = useChildren();
    const { notifications, markAsRead } = useNotifications(user, myChildren, loadingChildren);

    const handleNotificationClick = (notif) => {
        markAsRead(notif.id, notif.isDb);
        // Bisa tambahkan navigasi spesifik di sini jika diperlukan
    };

    if (loadingChildren) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex justify-center items-center">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-indigo-200 pb-28 pt-8 px-5 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[5%] right-[-10%] w-[50vw] h-[50vw] bg-indigo-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute bottom-[20%] left-[-20%] w-[60vw] h-[60vw] bg-purple-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-md mx-auto space-y-6">
                <div className="text-center mb-8">
                    <div className="relative inline-block">
                        <div className="absolute inset-0 bg-indigo-200 blur-xl opacity-50 rounded-full"></div>
                        <div className="relative w-16 h-16 bg-white/60 backdrop-blur-md rounded-[1.25rem] flex items-center justify-center text-3xl mx-auto mb-4 shadow-sm border border-white">
                            🔔
                        </div>
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">Pusat Notifikasi</h2>
                    <p className="text-sm text-gray-500 mt-2">Pengingat pintar untuk tumbuh kembang anak.</p>
                </div>

                {notifications.length > 0 ? (
                    <div className="space-y-4">
                        {notifications.map((notif) => (
                            <NotificationItem key={notif.id} {...notif} onClick={() => handleNotificationClick(notif)} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 px-6 bg-white/50 backdrop-blur-xl rounded-[2.5rem] border border-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                        <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl mx-auto mb-6 shadow-sm grayscale opacity-50">📭</div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2 tracking-tight">Belum Ada Notifikasi</h3>
                        <p className="text-sm text-gray-500 max-w-xs mx-auto leading-relaxed">
                            Saat ini belum ada pengingat baru. Kami akan memberitahu Anda jika ada jadwal penting.
                        </p>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
            `}</style>
        </div>
    );
}
